import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { object, date, number } from "yup";
import { Form, Button, Row, Space } from "@masterteam/mt-design-react";

import AdminFormModal from "../../AdminFormModal";
import api from "../../../providers/api";
import {
  addToast,
  YesNoOptions,
  UnitOfMeasureForInitiative
} from "../../../helpers";

import "./style.scss";

const { Field } = Form;
const numberRegex = /^\d{0,19}(\.\d{1,2})?$/;
const percentageRegex = /^(\d{0,2}|100)(\.\d{1,2})?$/;

const validationSchema = object().shape({
  Value: number(),
  Modified: date(),
  UnitOfMeasure: object().required(" "),
  BooleanValue: object().nullable()
});

const UpdateValueForm = ({
  data,
  closeModal,
  initiativeData = null,
  getData = () => {}
}) => {
  const intl = useIntl();
  const isAr = intl.locale === "ar";

  const [loading, setLoading] = useState(true);
  const [initialValues, setInitialValues] = useState({
    Value: 0,
    Modified: new Date(),
    UnitOfMeasure: UnitOfMeasureForInitiative[0],
    BooleanValue: null
  });

  useEffect(() => {
    if (data) {
      setInitialValues({
        ...data,
        Modified: new Date(data?.Modified),
        BooleanValue: YesNoOptions.find((e) => e.Value === data.BooleanValue),
        UnitOfMeasure: UnitOfMeasureForInitiative.find(
          (e) => e.value === data.UnitOfMeasure
        )
      });
    }
    setLoading(false);
  }, []);

  const onSubmit = async (values, { setSubmitting }) => {
    let body = {
      ...values,
      UnitOfMeasure: values.UnitOfMeasure.value,
      BooleanValue:
        values?.BooleanValue?.Value === null
          ? null
          : values?.BooleanValue?.Value,
      ID: data.ID,
      InitiativeID: initiativeData.ID
    };

    body = { ...body, InitiativeID: initiativeData.ID };
    try {
      const res = await api.initiatives.updateKPIValue(body);
      const { StatusCode } = res;
      if (StatusCode === "Success") {
        addToast.success(data ? "toast.successUpdate" : "toast.successSave");
        getData();
        closeModal();
      }
    } catch (error) {
      addToast.error(error?.message, {
        toastId: "get-users-error"
      });
    } finally {
      setSubmitting(false);
    }
    closeModal();
  };

  return (
    <AdminFormModal
      title={intl.formatMessage({
        id: "initiative.update-performance-indicator-value"
      })}
      isLoading={loading}
      onClose={closeModal}
    >
      <Form
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ values, errors, isSubmitting, isValid, setFieldValue }) => (
          <>
            <Space
              direction="vertical"
              size="medium"
              className="performance-indicator-form-body"
            >
              <Space direction="vertical" className="container" size="large">
                <Row size="small">
                  {values.UnitOfMeasure.value !== "MultipleChoice" ? (
                    <Field
                      type="number"
                      label={intl.formatMessage({
                        id: "common.current-value"
                      })}
                      inputProps={{
                        onKeyDown: (evt) => {
                          if (
                            evt.key === "e" ||
                            evt.key === "-" ||
                            evt.key === "+"
                          ) {
                            evt.preventDefault();
                          }
                        },
                        placeholder: intl.formatMessage({
                          id: "common.current-value"
                        }),
                        value: data.Value
                      }}
                      disabled={true}
                      hideErrorMessage
                    />
                  ) : (
                    <Field
                      type="select"
                      label={intl.formatMessage({
                        id: "common.current-value"
                      })}
                      inputProps={{
                        options: YesNoOptions,
                        menuPlacement: "top",
                        getOptionLabel: (el) =>
                          isAr ? el.ArabicName : el.EnglishName,
                        getOptionValue: (el) => el?.Value,
                        placeholder: intl.formatMessage({
                          id: "common.current-value"
                        }),
                        value: YesNoOptions.find(
                          (e) => e.Value === data.BooleanValue
                        )
                      }}
                      disabled={true}
                      hideErrorMessage
                    />
                  )}

                  {values.UnitOfMeasure.value !== "MultipleChoice" ? (
                    <Field
                      type="number"
                      name="Value"
                      label={intl.formatMessage({
                        id: "common.new-value"
                      })}
                      inputProps={{
                        onKeyDown: (evt) => {
                          if (
                            evt.key === "e" ||
                            evt.key === "-" ||
                            evt.key === "+"
                          ) {
                            evt.preventDefault();
                          }
                        },
                        onChange: (e) => {
                          if (values.UnitOfMeasure.value === "Percentage") {
                            if (percentageRegex.test(e.target.value))
                              setFieldValue("Value", e.target.value);
                          } else {
                            if (numberRegex.test(e.target.value))
                              setFieldValue("Value", e.target.value);
                          }
                        },
                        placeholder: intl.formatMessage({
                          id: "common.new-value"
                        })
                      }}
                      hideErrorMessage
                    />
                  ) : (
                    <Field
                      type="select"
                      label={intl.formatMessage({
                        id: "common.new-value"
                      })}
                      name="BooleanValue"
                      inputProps={{
                        options: YesNoOptions,
                        menuPlacement: "top",
                        getOptionLabel: (el) =>
                          isAr ? el.ArabicName : el.EnglishName,
                        getOptionValue: (el) => el?.Value,
                        placeholder: intl.formatMessage({
                          id: "common.new-value"
                        })
                      }}
                      hideErrorMessage
                    />
                  )}
                </Row>
                <Row size="small">
                  <Field
                    type="date"
                    name="Modified"
                    disabled={true}
                    label={intl.formatMessage({
                      id: "common.date"
                    })}
                    hideErrorMessage
                  />
                  <div style={{ width: "100%" }} />
                </Row>
              </Space>
              <Row justify="end" className="form-footer">
                <Button
                  loading={isSubmitting}
                  disabled={!isValid}
                  variant="primary"
                  type="submit"
                  fixed
                >
                  {intl.formatMessage({
                    id:  "common.save" 
                  })}
                </Button>
              </Row>
            </Space>
          </>
        )}
      </Form>
    </AdminFormModal>
  );
};

export default UpdateValueForm;
